if (!customElements.get('rone-onetime-builder')) {
  // Every network call in this flow (Loop's API, Shopify's cart/discount
  // routes) is otherwise unbounded — under real load a slow response would
  // leave the CTA stuck on "Applying discount…"/"Adding…" forever with no
  // way out. Aborting after FETCH_TIMEOUT_MS turns a hang into a normal
  // failure that the existing try/catch paths already handle gracefully.
  const FETCH_TIMEOUT_MS = 8000;
  const fetchWithTimeout = (url, options) => {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    return fetch(url, { ...options, signal: controller.signal }).finally(() => window.clearTimeout(timer));
  };

  customElements.define(
    'rone-onetime-builder',
    class RoneOnetimeBuilder extends HTMLElement {
      connectedCallback() {
        this.offerName = this.dataset.offerName || 'RONE-OT';
        this.bundleId = this.dataset.bundleId || '';
        this.shopDomain = this.dataset.shopDomain || '';
        this.picks = {};
        this.cartAdded = false;
        this.cartAdding = false;

        // Loop's real Bundle (Acquire > Bundles, purchaseType "ALL") is fetched
        // live purely to read its quantity-tier discounts (for the on-page
        // preview) and its box size id — no percentages are stored in the
        // theme at all. Per Loop's docs (create-bundle-transaction):
        // sellingPlanShopifyId is explicitly documented as "null for
        // one-time" — this is a genuine one-time purchase, so it stays null
        // rather than attaching the bundle's linked "Volume Discount" plan,
        // which is a real recurring-monthly selling plan and would enroll a
        // one-time buyer in an unwanted subscription.
        this.discounts = [];
        this.boxSizeId = '';
        this.redirectionUrl = '/checkout';
        this.resolvedBundleId = '';

        this.rows = Array.from(this.querySelectorAll('[data-flavor-row]'));
        this.qFill = this.querySelector('[data-q-fill]');
        this.qNodes = Array.from(this.querySelectorAll('[data-q-node]'));
        // A representative per-bag price for the slider's node previews —
        // the lowest real variant price on this product, since the discount
        // tiers themselves (fetched live below) are what actually vary by
        // quantity, not this baseline.
        const rowPrices = this.rows
          .map((row) => parseFloat(row.dataset.price || '0') / 100)
          .filter((price) => price > 0);
        this.qBasePrice = rowPrices.length ? Math.min(...rowPrices) : 0;
        this.sumRow = this.querySelector('[data-sum-row]');
        this.savePct = this.querySelector('[data-save-pct]');
        this.bagCount = this.querySelector('[data-bag-count]');
        this.cta = this.querySelector('[data-cta-link]');
        this.ctaLabel = this.querySelector('[data-cta-label]');
        this.ctaPriceWrap = this.querySelector('[data-cta-price]');
        this.ctaMsrp = this.querySelector('[data-cta-msrp]');
        this.ctaTotal = this.querySelector('[data-cta-total]');

        this.rows.forEach((row) => {
          const variantId = row.dataset.variantId;
          const addBtn = row.querySelector('[data-flavor-add]');
          const incBtn = row.querySelector('[data-flavor-inc]');
          const decBtn = row.querySelector('[data-flavor-dec]');
          if (addBtn) addBtn.addEventListener('click', () => this.adjust(variantId, 1));
          if (incBtn) incBtn.addEventListener('click', () => this.adjust(variantId, 1));
          if (decBtn) decBtn.addEventListener('click', () => this.adjust(variantId, -1));
        });

        if (this.cta) {
          this.cta.addEventListener('click', (event) => this.handleCtaClick(event));
        }

        this.render();
        this.bundleLoadPromise = this.loadBundle();
        this.suppressCartDrawer();
      }

      async resolveBundleId() {
        // data-bundle-id (from the section's schema setting) is trusted
        // directly whenever it's set — no format validation — since the
        // merchant explicitly configured it. Only falls back to the List
        // Bundles API when it's left blank.
        if (this.bundleId) {
          return this.bundleId;
        }

        const listUrl = 'https://api.loopsubscriptions.com/storefront/2023-10/bundle'
          + '?myshopifyDomain=' + encodeURIComponent(this.shopDomain);
        const response = await fetchWithTimeout(listUrl, { headers: { Accept: 'application/json' } });
        const data = await response.json();
        const bundles = (data && data.data) || [];
        if (!data || !data.success || !bundles.length) {
          throw new Error('No Loop bundles found for this store');
        }
        return bundles[0].id;
      }

      async loadBundle() {
        if (!this.shopDomain) return;

        try {
          this.resolvedBundleId = await this.resolveBundleId();

          const url = 'https://api.loopsubscriptions.com/storefront/2023-10/bundle/'
            + this.resolvedBundleId + '?myshopifyDomain=' + encodeURIComponent(this.shopDomain);
          const response = await fetchWithTimeout(url, { headers: { Accept: 'application/json' } });
          const data = await response.json();
          // // console.log('[rone-onetime-builder] GET bundle response:', JSON.stringify(data, null, 2));
          const bundle = data && data.data;
          if (!data || !data.success || !bundle) throw new Error('Bad Loop bundle response');

          this.discounts = bundle.discounts || [];
          this.boxSizeId = (bundle.boxSizes && bundle.boxSizes[0] && bundle.boxSizes[0].id) || '';
          this.redirectionUrl = bundle.redirectionUrl || '/checkout';
          // // console.log('[rone-onetime-builder] resolved boxSizeId:', this.boxSizeId, '- discounts:', JSON.stringify(this.discounts), '- redirectionUrl:', this.redirectionUrl);
          this.render();
        } catch (error) {
          console.warn('[rone-onetime-builder] could not load Loop bundle — checkout will proceed without a discount:', error);
          // Preview just won't reflect Loop's discount tiers until this
          // succeeds; rows still work, and checkout below just won't create a
          // bundle transaction rather than silently claiming a discount that
          // was never actually applied.
          this.resolvedBundleId = '';
        }
      }

      count() {
        return Object.values(this.picks).reduce((sum, n) => sum + n, 0);
      }

      clearCart() {
        return fetch('/cart/clear.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        }).catch(function () {});
      }

      bagWord(n) {
        return n + ' bag' + (n === 1 ? '' : 's');
      }

      // Highest quantity-threshold tier the current count qualifies for.
      discountTierFor(n) {
        let best = null;
        this.discounts.forEach((d) => {
          if (d.minCartQuantity <= n && (!best || d.minCartQuantity > best.minCartQuantity)) {
            best = d;
          }
        });
        return best;
      }

      applyTier(rawTotal, tier, itemCount) {
        if (!tier) return rawTotal;
        const value = parseFloat(tier.value) || 0;
        if (tier.type === 'FIXED_AMOUNT' || tier.type === 'FIXED') {
          // Loop = "apply discount on each eligible item", so value is
          // per-item. Scale by qty to mirror what checkout deducts.
          return Math.max(0, rawTotal - value * (itemCount || 0));
        }
        return rawTotal * (1 - value / 100);
      }

      adjust(variantId, delta) {
        const next = (this.picks[variantId] || 0) + delta;
        if (next <= 0) delete this.picks[variantId];
        else this.picks[variantId] = next;
        // Picks changed since the cart was last synced — the next checkout
        // click needs to add these before proceeding.
        this.cartAdded = false;
        this.render();
      }

      // Mirrors the Javy-style 1-2-3-4 quantity ladder: the fill line and
      // node states track the real total bag count (clamped to the 4
      // visible nodes), and each node's price/discount preview reuses the
      // exact same live-tier math as the CTA total below, just simulated at
      // that node's quantity rather than the shopper's actual picks.
      updateQSlider(n) {
        if (!this.qFill || !this.qNodes.length) return;

        const clamped = Math.min(n, 4);
        const fillPct = n === 0 ? 0 : ((clamped - 1) / 3) * 100;
        this.qFill.style.width = fillPct + '%';

        this.qNodes.forEach((node) => {
          const q = parseInt(node.dataset.q, 10);
          node.classList.toggle('rone-q-node--reached', n > 0 && q <= clamped);
          node.classList.toggle('rone-q-node--current', n > 0 && q === clamped);

          if (!this.qBasePrice) return;
          const rawTotal = this.qBasePrice * q;
          const tier = this.discountTierFor(q);
          const total = this.applyTier(rawTotal, tier,q);
          const pct = rawTotal > 0 ? Math.round(((rawTotal - total) / rawTotal) * 100) : 0;

          const priceEl = node.querySelector('[data-q-price]');
          const pctEl = node.querySelector('[data-q-pct]');
          if (priceEl) priceEl.textContent = '$' + (total / q).toFixed(2) + '/ea';
          if (pctEl) pctEl.textContent = pct > 0 ? pct + '% Off' : '';
        });
      }

      render() {
        const n = this.count();
        this.updateQSlider(n);

        this.rows.forEach((row) => {
          const variantId = row.dataset.variantId;
          const qty = this.picks[variantId] || 0;
          const countEl = row.querySelector('[data-flavor-count]');
          if (countEl) countEl.textContent = qty;
          row.classList.toggle('rone-frow--picked', qty > 0);
          const addBtn = row.querySelector('[data-flavor-add]');
          if (addBtn) addBtn.classList.toggle('rone-frow__add--hide', qty > 0);
          const step = row.querySelector('.rone-frow__step');
          if (step) step.classList.toggle('rone-frow__step--on', qty > 0);
        });

        if (n === 0) {
          if (this.cta) this.cta.classList.add('rone-cta--disabled');
          if (this.ctaLabel) this.ctaLabel.textContent = 'Select at least 1 bag';
          if (this.ctaPriceWrap) this.ctaPriceWrap.hidden = true;
          if (this.sumRow) this.sumRow.hidden = true;
          return;
        }

        let rawTotal = 0;
        this.rows.forEach((row) => {
          const variantId = row.dataset.variantId;
          const qty = this.picks[variantId] || 0;
          if (!qty) return;
          const price = parseFloat(row.dataset.price || '0') / 100;
          rawTotal += price * qty;
        });

        const tier = this.discountTierFor(n);
        const total = this.applyTier(rawTotal, tier,n);
        const pct = rawTotal > 0 ? Math.round(((rawTotal - total) / rawTotal) * 100) : 0;

        if (this.sumRow) this.sumRow.hidden = false;
        if (this.savePct) this.savePct.textContent = pct + '%';
        if (this.bagCount) this.bagCount.textContent = this.bagWord(n) + ' selected';
        if (this.cta) this.cta.classList.remove('rone-cta--disabled');
        if (this.ctaLabel) this.ctaLabel.textContent = 'Continue to Checkout';
        if (this.ctaPriceWrap) this.ctaPriceWrap.hidden = false;
        if (this.ctaMsrp) this.ctaMsrp.textContent = '$' + rawTotal.toFixed(2);
        if (this.ctaTotal) this.ctaTotal.textContent = '$' + total.toFixed(2);
      }

      async handleCtaClick(event) {
        if (this.cta.classList.contains('rone-cta--disabled')) {
          event.preventDefault();
          return;
        }
        if (this.cartAdded || this.cartAdding) return;
        event.preventDefault();
        await this.addToCart();
      }

      // POST .../bundle/{id}/transaction — required every time a new bundle
      // is added to the cart (per Loop's docs). productVariants[].quantity is
      // each child variant's own quantity inside the box; top-level `quantity`
      // is the number of *bundles*, which stays 1 for a build-your-own box —
      // passing the total bag count there would corrupt the order instead of
      // just failing to discount it.
      async createBundleTransaction(tier, productVariants) {
        if (!this.resolvedBundleId || !this.boxSizeId) {
          throw new Error('Loop bundle not loaded');
        }

        const url = 'https://api.loopsubscriptions.com/storefront/2023-10/bundle/'
          + this.resolvedBundleId + '/transaction?myshopifyDomain=' + encodeURIComponent(this.shopDomain);

        const body = {
          boxSizeId: this.boxSizeId,
          sellingPlanShopifyId: null,
          quantity: 1,
          productVariants,
        };
        if (tier && tier.id) body.discountId = tier.id;

        // // console.log('[rone-onetime-builder] POST create transaction — url:', url, '- body:', JSON.stringify(body), '- matched tier:', JSON.stringify(tier));

        const response = await fetchWithTimeout(url, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await response.json();
        // // console.log('[rone-onetime-builder] POST create transaction response:', JSON.stringify(data, null, 2));
        if (!data || !data.success || !data.data || !data.data.txnId) {
          throw new Error('Could not create Loop bundle transaction');
        }
        return data.data.txnId;
      }
      // This page adds to cart then immediately redirects to checkout, so
      // Upcart's cart drawer (which auto-opens on /cart/add.js) must never
      // get a chance to render. Subscribing here in connectedCallback —
      // before the shopper can even click add-to-cart — beats the drawer's
      // own open event, unlike subscribing right before the redirect, which
      // is too late since Upcart's script may already be loaded and armed.
      // Poll briefly in case Upcart's embed script hasn't defined these
      // globals yet at this point. Capped — if the app is uninstalled,
      // blocked, or its script fails to load, this must not poll forever.
      suppressCartDrawer() {
        var attempts = 0;
        var maxAttempts = 100; // ~5s at 50ms
        var trySubscribe = function () {
          if (typeof window.upcartSubscribeCartOpened !== 'function') {
            attempts += 1;
            if (attempts >= maxAttempts) {
              console.warn('[rone-onetime-builder] Upcart API never became available — giving up on cart-drawer suppression.');
              return;
            }
            window.setTimeout(trySubscribe, 50);
            return;
          }
          window.upcartSubscribeCartOpened(function () {
            if (typeof window.upcartCloseCart === 'function') window.upcartCloseCart();
          });
          if (typeof window.upcartSubscribeCartLoaded === 'function') {
            window.upcartSubscribeCartLoaded(function () {
              var stickyBtn = document.querySelector('#upCartStickyButton') || document.getElementById('CartPopup');
              if (stickyBtn) stickyBtn.style.display = 'none';
            });
          }
        };
        trySubscribe();
      }

      async addToCart() {
        await this.clearCart();
        if (this.cartAdding || this.cartAdded) return;
        
        const variantIds = Object.keys(this.picks);
        if (!variantIds.length) return;

        this.cartAdding = true;
        this.ctaLabel && (this.ctaLabel.textContent = 'Applying discount…');

        // Wait for the bundle fetch so the box size id / discount tiers
        // matching the final picked quantity are ready before the
        // transaction is created.
        await this.bundleLoadPromise;

        const productVariants = variantIds.map((id) => ({
          shopifyId: parseInt(id, 10),
          quantity: this.picks[id],
        }));
        const tier = this.discountTierFor(this.count());
        // console.log('[rone-onetime-builder] STEP 0 — bag count:', this.count(), '- matched tier:', JSON.stringify(tier));

        let txnId = '';
        try {
          txnId = await this.createBundleTransaction(tier, productVariants);
          // console.log('[rone-onetime-builder] STEP 1 — bundle transaction created — txnId:', txnId);
        } catch (error) {
          console.warn('[rone-onetime-builder] STEP 1 FAILED — could not create Loop bundle transaction — checkout will proceed without a discount:', error);
        }

        const items = variantIds.map((id) => {
          const item = { id: parseInt(id, 10), quantity: this.picks[id] };
          if (txnId) item.properties = { _bundleId: txnId };
          return item;
        });
        // console.log('[rone-onetime-builder] STEP 2 — POST /cart/add.js — items:', JSON.stringify(items));
        try {
          // Step 1 — add the picked variants, each tagged with the bundle
          // txnId as the _bundleId line-item property (Loop's own linking
          // mechanism between a cart line and its bundle transaction).
          const res = await fetchWithTimeout('/cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({ items }),
          });
          const data = await res.json();
          // console.log('[rone-onetime-builder] STEP 2 response — status:', res.status, res.ok, '- body:', JSON.stringify(data));
          if (!res.ok || data.status) throw data.description || 'Could not add to cart';
          this.cartAdded = true;

          // Step 2 — apply the discount, per Loop's own guidance: a plain
          // GET to Shopify's /discount/:code route (the same route used in
          // discount links) sets the discount on the checkout session via
          // its Set-Cookie response — no response body to check, just await
          // it so the cookie lands before we navigate to checkout. Relative
          // path so it stays same-origin. redirect: 'manual' stops fetch
          // from chasing the 302 this route sends back — that hop's target
          // can be cross-origin (e.g. the real myshopify.com domain behind
          // a local dev proxy) and following it under the default 'follow'
          // mode is what was hitting CORS; the discount cookie is already
          // set by the first, same-origin response, so there's nothing
          // useful to read from the redirect target anyway.
          // Only ever applied while the shopper is actually on the onetime
          // stage (?step=onetime) right now — checked directly off the
          // live URL, not any stored flag. Otherwise (e.g. ?step=flavors&
          // quantity=2&frequency=30) this discount code would end up
          // applied alongside a subscription checkout in the same browser
          // session, merging the two into one recurring order instead of
          // keeping onetime and subscription purchases separate.
          var onOnetimeStage = new URLSearchParams(window.location.search).get('step') === 'onetime';

          if (tier && tier.code && onOnetimeStage) {
            // console.log('[rone-onetime-builder] STEP 3 — GET /discount/:code —', tier.code);
            try {
              await fetchWithTimeout(`/discount/${encodeURIComponent(tier.code)}`, {
                method: 'GET',
                credentials: 'same-origin',
                redirect: 'manual',
              });
            } catch (error) {
              // A hiccup applying the discount shouldn't block checkout —
              // the items are already in the cart at this point.
              console.warn('[rone-onetime-builder] STEP 3 FAILED — discount not applied, proceeding to checkout anyway:', error);
            }
          } else if (tier && tier.code && !onOnetimeStage) {
            console.warn('[rone-onetime-builder] STEP 3 SKIPPED — not on the onetime stage (?step=onetime), not applying the discount code:', tier.code);
          } else {
            // console.log('[rone-onetime-builder] STEP 3 SKIPPED — no matched tier or tier.code:', JSON.stringify(tier));
          }

          // Step 3 — redirect to checkout.
          const checkoutUrl = this.redirectionUrl || '/checkout';
          if (this.ctaLabel) this.ctaLabel.textContent = 'Continue to Checkout';
          if (typeof window.upcartCloseCart === 'function') window.upcartCloseCart();
          window.location.href = checkoutUrl;
        } catch (error) {
          console.error('[rone-onetime-builder] FAILED:', typeof error === 'string' ? error : error);
          if (this.ctaLabel) this.ctaLabel.textContent = 'Continue to Checkout';
        } finally {
          this.cartAdding = false;
        }
      }
    }
  );
}
